/**
 * **Booking flows and bookings — the staff client** (C19, FR-J11, ADR-0037
 * §6; reshaped by C20, ADR-0040 §5, Wave 20C, T-5.68). The flow's shape is
 * waves.md Wave 20 **I-2**, written out in full in
 * `docs/sessions/2026-09-27-w20a-service.md` §2.
 *
 * A flow is **one form in four parts** — what we ask, what they can book,
 * payment, and wording. There is no step library and no ordering: the order a
 * visitor meets is fixed by ADR-0040 §1 (details with a code in place, choose,
 * pay, done).
 *
 * **The service is the one validator** (`app/domain/booking.py:
 * validate_definition`), and every refusal comes back in the firm's words
 * (`ApiError.message`). `flowProblems` reads the same few rules from the draft
 * **before** Save so the edit bar can say why Save is off. It is a courtesy,
 * never a gate the service relies on.
 *
 * The limits, the defaults, the question kinds and whether a mobile code can
 * be sent are `GET /booking-flows/vocabulary`, not a second list here.
 *
 * **C25 (Wave 27, waves.md Wave 27 I-1 and I-4)** — one payment method per
 * flow (`none · gateway · qr`), the QR's picture and payee text, *payment proof
 * required*, up to five documents, and staff's *Confirm payment* and *Cancel*
 * on every unconfirmed booking. The flow's new fields are **optional in these
 * types** only so an editor and its fixtures written before them still type:
 * the service sends every one from Wave 27, and `paymentModeOf` reads a flow
 * either way.
 */

import { ApiError, api, type Envelope } from '../api'
import type { ManualReceiptMode } from './billing'

// -- shapes ------------------------------------------------------------------

export type QuestionKind = 'text' | 'long_text' | 'select' | 'date' | 'checkbox'
/** An answer's kind, as a booking's record reads it — the same five. */
export type FieldKind = QuestionKind
export type FlowStatus = 'draft' | 'live' | 'paused'
export type BookingStatus =
  | 'started'
  | 'awaiting_payment'
  | 'payment_review'
  | 'confirmed'
  | 'cancelled'
  | 'expired'
  | 'request'

/** The list's filter order. `payment_review` — *Proof to check* — sits by the
 *  other waits (C25, ADR-0052 §7). */
export const BOOKING_STATUSES: BookingStatus[] = [
  'started',
  'awaiting_payment',
  'payment_review',
  'confirmed',
  'request',
  'cancelled',
  'expired',
]

/** ADR-0052 §1 — one payment method per flow. */
export type PaymentMode = 'none' | 'gateway' | 'qr'
export const PAYMENT_MODES: PaymentMode[] = ['none', 'gateway', 'qr']

/** A bank QR's picture — one of the firm's own `/site-media/` pictures — and
 *  its optional payee text (I-1). Read live at the pay step. */
export interface PaymentQr {
  image_url: string | null
  upi_id: string | null
  account_name: string | null
  account_number: string | null
  ifsc: string | null
  bank_name: string | null
}
export type PayeeField = Exclude<keyof PaymentQr, 'image_url'>
export const PAYEE_FIELDS: PayeeField[] = ['upi_id', 'account_name', 'account_number', 'ifsc', 'bank_name']

/** ADR-0053 §1 — one document a flow asks for. `key` is
 *  `^[a-z][a-z0-9_]{0,31}$`; `payment_proof` is reserved. */
export interface FlowDocument {
  key: string
  label: string
  hint: string
  required: boolean
}
export const MAX_DOCUMENTS = 5
export const PROOF_SLOT = 'payment_proof'

export interface FlowQuestion {
  key: string
  label: string
  kind: QuestionKind
  required: boolean
  /** A `select` only. */
  options?: string[]
}

/** Card 1 — *what we ask*. Name and email are always asked and not listed. */
export interface FlowAsk {
  mobile: boolean
  verify_mobile: boolean
  questions: FlowQuestion[]
}

/** One offered service. `plan_ids: []` is every active plan of it. */
export interface FlowOfferService {
  service_id: string
  plan_ids: string[]
}

/** Card 2 — *what they can book*. `services: []` is the firm's site-visible
 *  catalog. */
export interface FlowOffer {
  services: FlowOfferService[]
}

export type WordingKey = 'title' | 'intro' | 'thanks' | 'send_code' | 'continue' | 'pay' | 'done'
export const WORDING_KEYS: WordingKey[] = ['title', 'intro', 'thanks', 'send_code', 'continue', 'pay', 'done']
/** C25 (I-1) — the bank-QR pay step's button, and what the page says while the
 *  firm checks a proof. Kept apart from `WORDING_KEYS` so the editor written
 *  before C25 draws what it always drew; 27C's payment card draws these. */
export type QrWordingKey = 'submit_proof' | 'checking'
export const QR_WORDING_KEYS: QrWordingKey[] = ['submit_proof', 'checking']
export type FlowWording = Record<WordingKey, string> & Partial<Record<QrWordingKey, string>>

/** The C25 part of a flow (I-1). Optional — see the module comment. */
export interface FlowPayment {
  payment_mode?: PaymentMode
  payment_qr?: PaymentQr | null
  proof_required?: boolean
  documents?: FlowDocument[]
}

export interface BookingFlow extends FlowPayment {
  id: string
  slug: string
  name: string
  status: FlowStatus
  /** `/book/<slug>` — on the firm's own domain. */
  path: string
  ask: FlowAsk
  offer: FlowOffer
  /** Paid at all — by the gateway or by bank QR. The service derives it from
   *  `payment_mode` and still reads it alone from an old editor (I-1). */
  payment: boolean
  create_case: boolean
  wording: FlowWording
  bookings: number | null
  created_at: string | null
  updated_at: string | null
}

export interface MobileChannel {
  available: boolean
  channel: 'sms' | 'whatsapp' | null
  /** The line beside a disabled *Verify mobile by code*. */
  reason: string | null
}

export interface FlowDefinition extends FlowPayment {
  ask: FlowAsk
  offer: FlowOffer
  /** A body with only this, and no `payment_mode`, is read as gateway or none
   *  (I-1); when both are sent, `payment_mode` wins. */
  payment: boolean
  create_case: boolean
  wording: FlowWording
}

export interface FlowVocabulary {
  question_kinds: QuestionKind[]
  max_questions: number
  max_options: number
  wording: Record<WordingKey, { max: number; default: string }> &
    Partial<Record<QrWordingKey, { max: number; default: string }>>
  statuses: FlowStatus[]
  slug_pattern: string
  default: FlowDefinition
  mobile_channel: MobileChannel
  /** C25 (I-1). */
  payment_modes?: PaymentMode[]
  payee_fields?: Record<PayeeField, { max: number }>
  max_documents?: number
  document_limits?: { label: number; hint: number }
  proof_slot?: string
}

/** What Save sends — the whole flow, one call. */
export interface FlowBody extends FlowDefinition {
  slug: string
  name: string
  status: FlowStatus
}

export interface BookingRow {
  id: string
  flow_id: string
  flow_slug: string
  flow_name: string | null
  client_id: string
  client_name: string | null
  status: BookingStatus
  service_id: string | null
  service_name: string | null
  plan_id: string | null
  plan_name: string | null
  amount: string | null
  currency: string | null
  invoice_id: string | null
  invoice_number: string | null
  created_at: string | null
  confirmed_at: string | null
}

export interface BookingAnswer {
  key: string
  label: string
  kind: FieldKind
  value: unknown
}

export interface BookingDetail extends BookingRow {
  answers: BookingAnswer[]
  person_id: string | null
  /** w20a — who verified, and so who pays. */
  contact?: { name: string | null; email: string | null; mobile: string | null; mobile_verified: boolean }
  receipt_id: string | null
  case_id: string | null
  request_id: string | null
  payment: {
    invoice_status: string | null
    grand_total: string | null
    outstanding: string | null
  }
  last_error: string | null
  submitted_at: string | null
  closed_at: string | null
  flow: { name: string | null; payment: boolean; payment_mode?: PaymentMode; create_case: boolean }
  // -- C25, I-4 (27B fills these) ------------------------------------------
  /** What staff may do, answered by the service (ADR-0052 §4). */
  allowed?: BookingAllowed
  /** The UPI or bank reference the visitor typed beside a QR proof. */
  payment_reference?: string | null
  proof_submitted_at?: string | null
  cancel_reason?: string | null
  /** What the visitor sent with it (ADR-0053 §5). */
  documents?: BookingDocument[]
}

/** I-4 — `confirm_payment` and `cancel` on every unconfirmed booking;
 *  `cancel_credits_invoice` when a cancel would write a full credit note. */
export interface BookingAllowed {
  confirm_payment: boolean
  cancel: boolean
  cancel_credits_invoice: boolean
}

/** I-4 — a document a booking brought, by its slot. */
export interface BookingDocument {
  id: string
  slot: string
  title: string | null
  content_type: string
  size_bytes: number
}

/** I-4 — `POST /bookings/{id}/confirm-payment`. `received_on` is a date not
 *  after today; `reference` is prefilled with the visitor's. */
export interface ConfirmPaymentBody {
  mode: ManualReceiptMode
  reference?: string | null
  received_on: string
}

/** I-4 — `POST /bookings/{id}/cancel`. A reason of 3 to 500 characters, which
 *  the client's email carries and no event does. */
export interface CancelBody {
  reason: string
}

/** The list's `meta` — `counts` per status since C25 (I-4, ADR-0052 §7). */
export interface BookingListMeta {
  count?: number
  limit?: number
  counts?: Partial<Record<BookingStatus, number>>
}

/**
 * *See it*, for staff — `GET /booking-flows/{id}/preview` (w19e, T-5.65).
 * **The public read's shape** (I-1), for any status, behind
 * `booking:can_view`. `price` follows the firm's *show prices* switch, and
 * `ask.verify_mobile` is effective — on only when a mobile code can be sent.
 */
export interface PreviewPlan {
  id: string
  name: string
  description: string | null
  price: string | null
  payable: boolean
  free: boolean
}
export interface PreviewService {
  id: string
  name: string
  description: string | null
  plans: PreviewPlan[]
}
export interface FlowPreview {
  slug: string
  name: string
  status: FlowStatus
  path: string
  ask: FlowAsk
  services: PreviewService[]
  payment: boolean
  /** C25 — as the public read carries them (I-2); the QR itself is read per booking. */
  payment_mode?: PaymentMode
  proof_required?: boolean
  documents?: FlowDocument[]
  wording: FlowWording
  brand: { name: string; logo_url: string | null }
  /** A paused flow's *not taking bookings* line — what visitors see today. */
  message: string | null
  preview?: boolean
}

// -- the client --------------------------------------------------------------

export const booking = {
  flows: () => api<BookingFlow[]>('/api/v1/booking-flows'),
  vocabulary: () => api<FlowVocabulary>('/api/v1/booking-flows/vocabulary'),
  flow: (id: string) => api<BookingFlow>(`/api/v1/booking-flows/${id}`),
  /** Staff's *See it* — any status, a draft included (w19e). */
  preview: (id: string) => api<FlowPreview>(`/api/v1/booking-flows/${id}/preview`),
  createFlow: (body: FlowBody) => api<BookingFlow>('/api/v1/booking-flows', { method: 'POST', body }),
  updateFlow: (id: string, body: FlowBody) =>
    api<BookingFlow>(`/api/v1/booking-flows/${id}`, { method: 'PUT', body }),
  /** The service answers `204` with no body, which `api()` reads as a failed
   *  envelope — so a 2xx refusal of the envelope is the success it is. */
  deleteFlow: async (id: string): Promise<void> => {
    try {
      await api<null>(`/api/v1/booking-flows/${id}`, { method: 'DELETE' })
    } catch (err) {
      if (err instanceof ApiError && err.status >= 200 && err.status < 300) return
      throw err
    }
  },

  /** The newest 200. The list screen filters and searches what it holds
   *  (C14 §10.6 item 8: no server paging in the staff lists). */
  bookings: (query: { status?: string; flow_id?: string; limit?: number } = {}) => {
    const params = new URLSearchParams()
    if (query.status) params.set('status', query.status)
    if (query.flow_id) params.set('flow_id', query.flow_id)
    params.set('limit', String(query.limit ?? 200))
    return api<BookingRow[]>(`/api/v1/bookings?${params.toString()}`)
  },
  booking: (id: string) => api<BookingDetail>(`/api/v1/bookings/${id}`),

  // -- C25, I-4 — staff confirm and cancel (27B builds the routes) ------------

  /** *Confirm payment* (ADR-0052 §4). `stepUpAssertion` only when the record's
   *  `allowed` says an invoice will be written — the service asks for step-up
   *  then and only then. */
  confirmPayment: (id: string, body: ConfirmPaymentBody, stepUpAssertion?: string) =>
    api<BookingDetail>(`/api/v1/bookings/${id}/confirm-payment`, {
      method: 'POST',
      body,
      ...(stepUpAssertion ? { stepUpAssertion } : {}),
    }),
  /** *Cancel booking*, with a reason (ADR-0052 §4). `stepUpAssertion` only when
   *  `allowed.cancel_credits_invoice` — a credit note will be written. */
  cancel: (id: string, body: CancelBody, stepUpAssertion?: string) =>
    api<BookingDetail>(`/api/v1/bookings/${id}/cancel`, {
      method: 'POST',
      body,
      ...(stepUpAssertion ? { stepUpAssertion } : {}),
    }),
}

/** The per-status counts of a list answer (I-4) — `{}` from a service before C25. */
export function bookingCounts(envelope: Pick<Envelope<unknown>, 'meta'>): Partial<Record<BookingStatus, number>> {
  const meta = (envelope.meta ?? {}) as BookingListMeta
  return meta.counts ?? {}
}

/** How a flow is paid — `payment_mode`, or the old boolean read as gateway or
 *  none (I-1). */
export function paymentModeOf(flow: { payment?: boolean; payment_mode?: PaymentMode }): PaymentMode {
  return flow.payment_mode ?? (flow.payment === false ? 'none' : 'gateway')
}

export const PAYMENT_MODE_LABELS: Record<PaymentMode, string> = {
  none: 'No payment',
  gateway: 'Online, through your payment gateway',
  qr: 'Bank transfer to your QR',
}

// -- words -------------------------------------------------------------------

export const QUESTION_KIND_LABELS: Record<QuestionKind, string> = {
  text: 'Short answer',
  long_text: 'Long answer',
  select: 'Choice',
  date: 'Date',
  checkbox: 'Yes or no',
}
/** The same words, by the name the booking record uses. */
export const FIELD_KIND_LABELS = QUESTION_KIND_LABELS

export const WORDING_NAMES: Record<WordingKey, { label: string; hint: string }> = {
  title: { label: 'Page title', hint: 'The heading at the top of the booking page.' },
  intro: { label: 'Intro', hint: 'A line or two under the title. Optional.' },
  thanks: { label: 'Thank-you message', hint: 'What they read once the booking is made.' },
  send_code: { label: 'Send code button', hint: 'Sends the code to their email.' },
  continue: { label: 'Continue button', hint: 'Moves them on to the next part.' },
  pay: { label: 'Pay button', hint: 'Opens the payment.' },
  done: { label: 'Done button', hint: 'Closes the thank-you screen.' },
}

export const FLOW_STATUS_NOTES: Record<FlowStatus, string> = {
  draft: 'Not on your site. Only your firm can see it, in a preview.',
  live: 'Open for bookings at its address, and offered to website buttons.',
  paused: 'Its address says you are not taking bookings right now.',
}

/** The plain line at the top of the editor (T-5.68). */
export const FLOW_EXPLAINER =
  'Visitors fill in their details, confirm their email with a code, choose a plan and pay — all on your website.'

// -- the path a button stores ------------------------------------------------

export function bookingPath(slug: string, planId?: string | null): string {
  const base = `/book/${slug}`
  return planId ? `${base}?plan=${encodeURIComponent(planId)}` : base
}

/** A stored button link, read back — `null` when it is not a flow's. The
 *  Wave 19 address, `/client/book/<slug>`, is read too: it redirects. */
export function parseBookingPath(value: unknown): { slug: string; planId: string | null } | null {
  if (typeof value !== 'string') return null
  const match = /^(?:\/client)?\/book\/([a-z0-9][a-z0-9-]{0,63})(?:\?plan=([^&#]+))?$/.exec(value.trim())
  if (!match) return null
  return { slug: match[1] as string, planId: match[2] ? decodeURIComponent(match[2]) : null }
}

// -- the rules, before Save ----------------------------------------------------

const SLUG = /^[a-z0-9][a-z0-9-]{0,63}$/

/**
 * What is wrong with a draft, in the service's own words where it has them
 * (`validate_definition`). Empty when Save may be pressed. **The first is
 * what the edit bar shows.**
 */
export function flowProblems(
  body: FlowBody,
  limits: { max_questions: number; max_options: number } = { max_questions: 5, max_options: 20 },
): string[] {
  const out: string[] = []
  if (!body.name.trim()) out.push('A flow needs a name.')
  if (!SLUG.test(body.slug.trim().toLowerCase())) {
    out.push(
      'An address is lowercase letters, digits and hyphens, starting with a letter or digit, at most 64 characters.',
    )
  }
  if (body.ask.verify_mobile && !body.ask.mobile) out.push('Ask for the mobile before verifying it.')

  const questions = body.ask.questions
  if (questions.length > limits.max_questions) {
    out.push(`A flow asks at most ${limits.max_questions} extra questions.`)
  }
  questions.forEach((question, index) => {
    const n = index + 1
    if (!question.label.trim()) out.push(`Question ${n} needs a label.`)
    if (question.kind === 'select') {
      const options = (question.options ?? []).map((o) => o.trim()).filter(Boolean)
      if (options.length === 0) out.push(`Question ${n}: a choice needs at least one option.`)
      if (options.length > limits.max_options) {
        out.push(`Question ${n}: a choice has at most ${limits.max_options} options.`)
      }
      if (new Set(options).size !== options.length) out.push(`Question ${n}: an option is listed twice.`)
    }
  })
  return out
}

/**
 * The draft as Save sends it: the name trimmed, the address lowercased, a
 * choice's options one per line with the blank lines gone (the editor keeps a
 * blank line while the firm is typing the next option), no `options` on a
 * question that is not a choice, *verify mobile* off whenever the mobile is
 * not asked, and every wording text trimmed (an empty one takes the service's
 * default).
 */
export function cleanBody(values: FlowBody): FlowBody {
  return {
    ...values,
    name: values.name.trim(),
    slug: values.slug.trim().toLowerCase(),
    ask: {
      ...values.ask,
      verify_mobile: values.ask.mobile && values.ask.verify_mobile,
      questions: values.ask.questions.map(({ options, ...question }) =>
        question.kind === 'select'
          ? {
              ...question,
              label: question.label.trim(),
              options: (options ?? []).map((o) => o.trim()).filter(Boolean),
            }
          : { ...question, label: question.label.trim() },
      ),
    },
    wording: Object.fromEntries([
      ...WORDING_KEYS.map((key) => [key, (values.wording[key] ?? '').trim()]),
      // C25 — kept only when the draft carries them, so an older editor's body
      // is what it always was and the service fills their defaults.
      ...QR_WORDING_KEYS.filter((key) => values.wording[key] !== undefined).map((key) => [
        key,
        (values.wording[key] ?? '').trim(),
      ]),
    ]) as FlowWording,
  }
}

/** An address from a name: `Tax seminar 2026` → `tax-seminar-2026`. */
export function slugFromName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64)
    .replace(/-+$/g, '')
}

/** A fresh key for a new question: `question_1`, `question_2`, … unused. The
 *  firm never sees it; it is how the answer is stored. */
export function nextQuestionKey(questions: FlowQuestion[]): string {
  const taken = new Set(questions.map((q) => q.key))
  for (let n = questions.length + 1; ; n += 1) {
    const key = `question_${n}`
    if (!taken.has(key)) return key
  }
}

// -- what they can book ------------------------------------------------------------

type HasPlans = { plans?: Array<{ id: string; status: string; site_visible?: boolean }> }

/** A service's active plans, in the catalog's order. */
export function activePlanIds(service: HasPlans): string[] {
  return (service.plans ?? []).filter((p) => p.status === 'active').map((p) => p.id)
}

/** The plans `plan_ids: []` offers: active **and shown on the website**. C25,
 *  ADR-0054 §5 — a plan the firm keeps off its website is offered by a flow
 *  only when the flow names it (`domain/booking.offered`). */
export function shownPlanIds(service: HasPlans): string[] {
  return (service.plans ?? [])
    .filter((p) => p.status === 'active' && p.site_visible !== false)
    .map((p) => p.id)
}

/** Which of a service's plans are ticked: every one the website shows when
 *  the offer names none (`plan_ids: []`). */
export function tickedPlans(item: FlowOfferService, service: HasPlans): string[] {
  return item.plan_ids.length ? item.plan_ids : shownPlanIds(service)
}

/**
 * A plan ticked or unticked. Ticking exactly the plans the website shows
 * makes the list `[]` again — *every plan on the website*, which keeps a plan
 * the firm adds or shows later offered; any other set is named, which is how
 * a hidden plan is offered from a direct link (ADR-0054 §5). Unticking the last
 * ticked plan is refused (`null`): a service offers at least one plan, or is
 * unticked itself.
 */
export function togglePlan(
  item: FlowOfferService,
  service: HasPlans,
  planId: string,
  on: boolean,
): FlowOfferService | null {
  const all = activePlanIds(service)
  const ticked = new Set(tickedPlans(item, service))
  if (on) ticked.add(planId)
  else ticked.delete(planId)
  const kept = all.filter((id) => ticked.has(id))
  if (kept.length === 0) return null
  const shown = shownPlanIds(service)
  const everyShown = shown.length > 0 && kept.length === shown.length && shown.every((id) => ticked.has(id))
  return { ...item, plan_ids: everyShown ? [] : kept }
}
